import type { BrowserOidcConfig } from '../api/types';
import { normalizeReturnToPath } from './return-path';

const PENDING_KEY = 'event.oidc.pending';
const TRANSACTION_TTL_MS = 10 * 60 * 1000;
const CLOCK_SKEW_SECONDS = 30;

interface Discovery {
  readonly issuer: string;
  readonly authorization_endpoint: string;
  readonly token_endpoint: string;
  readonly jwks_uri: string;
  readonly end_session_endpoint?: string;
}

interface PendingLogin {
  readonly issuer: string;
  readonly clientId: string;
  readonly redirectUri: string;
  readonly state: string;
  readonly nonce: string;
  readonly verifier: string;
  readonly createdAt: number;
  readonly returnTo?: string;
}

interface LiveTokens {
  readonly accessToken: string;
  readonly refreshToken?: string;
  readonly idToken: string;
  readonly expiresAt: number;
  readonly subject: string;
  readonly nonce: string;
}

/** A temporary identity-provider outage blocks the request, not the identity.
 * Callers may retry; they must never fall back to the expired access token.
 */
export class BrowserOidcTemporaryError extends Error {
  readonly retryable = true;

  constructor(cause?: unknown) {
    super('oidc.temporarily_unavailable', { cause });
    this.name = 'BrowserOidcTemporaryError';
  }
}

interface BrowserOidcOptions {
  readonly origin: string;
  readonly storage: Storage;
  readonly crypto: Crypto;
  readonly fetcher?: typeof fetch;
  readonly now?: () => number;
}

function object(value: unknown): Record<string, unknown> {
  if (!value || typeof value !== 'object' || Array.isArray(value)) {
    throw new Error('oidc.invalid_response');
  }
  return value as Record<string, unknown>;
}

function text(value: unknown): string {
  if (typeof value !== 'string' || value.length === 0) {
    throw new Error('oidc.invalid_response');
  }
  return value;
}

function base64Url(bytes: Uint8Array): string {
  return btoa(String.fromCharCode(...bytes))
    .replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
}

function decodeBase64Url(value: string): Uint8Array {
  if (!/^[A-Za-z0-9_-]+$/.test(value)) throw new Error('oidc.invalid_id_token');
  const encoded = value.replace(/-/g, '+').replace(/_/g, '/');
  return Uint8Array.from(atob(encoded.padEnd(Math.ceil(encoded.length / 4) * 4, '=')), (c) => c.charCodeAt(0));
}

export async function createPkceChallenge(verifier: string, crypto: Crypto): Promise<string> {
  return base64Url(new Uint8Array(await crypto.subtle.digest('SHA-256', new TextEncoder().encode(verifier))));
}

/** Public-client OIDC. Only the short-lived PKCE transaction is persisted.
 * Access/refresh/ID tokens stay in this instance's memory. Authorization is
 * always derived from the API's /me, never from decoded browser claims.
 */
export class BrowserOidcClient {
  private readonly fetcher: typeof fetch;
  private readonly now: () => number;
  private metadata?: Discovery;
  private tokens?: LiveTokens;
  private epoch = 0;
  private controller = new AbortController();
  private refreshFlight?: Promise<string>;
  private pendingRefresh?: {
    readonly response: Record<string, unknown>;
    readonly previous: LiveTokens;
    readonly receivedAt: number;
  };
  private completion?: { readonly key: string; readonly promise: Promise<string | undefined> };

  constructor(private readonly config: BrowserOidcConfig, private readonly options: BrowserOidcOptions) {
    // Native Window.fetch is receiver-sensitive in browsers. A method call
    // on this client must not give it the BrowserOidcClient as its receiver.
    this.fetcher = options.fetcher ?? globalThis.fetch.bind(globalThis);
    this.now = options.now ?? Date.now;
    const issuer = new URL(config.issuer);
    if (issuer.protocol !== 'https:' || issuer.username || issuer.password || issuer.search || issuer.hash) {
      throw new Error('oidc.invalid_issuer');
    }
    if (!config.clientId) throw new Error('oidc.not_configured');
  }

  private endpoint(value: unknown): string {
    const url = new URL(text(value));
    if (url.protocol !== 'https:' || url.origin !== new URL(this.config.issuer).origin || url.username || url.password || url.hash) {
      throw new Error('oidc.invalid_endpoint');
    }
    return url.toString();
  }

  private random(): string {
    return base64Url(this.options.crypto.getRandomValues(new Uint8Array(32)));
  }

  private async json(url: string, init: RequestInit = {}): Promise<Record<string, unknown>> {
    const controller = this.controller;
    const timeout = new AbortController();
    const abort = () => timeout.abort();
    let timedOut = false;
    controller.signal.addEventListener('abort', abort, { once: true });
    if (controller.signal.aborted) timeout.abort();
    const timer = setTimeout(() => {
      timedOut = true;
      abort();
    }, 15_000);
    try {
      const response = await this.fetcher(url, { ...init, credentials: 'omit', cache: 'no-store', redirect: 'error', signal: timeout.signal });
      if (controller.signal.aborted) throw new Error('oidc.session_changed');
      if (timedOut) throw new BrowserOidcTemporaryError();
      if (response.status === 429 || response.status >= 500) {
        throw new BrowserOidcTemporaryError();
      }
      if (!response.ok) throw new Error('oidc.request_failed');
      const data: unknown = await response.json();
      if (controller.signal.aborted) throw new Error('oidc.session_changed');
      if (timedOut) throw new BrowserOidcTemporaryError();
      return object(data);
    } catch (error) {
      // Logout/identity change is an intentional abort, never a retryable outage.
      if (controller.signal.aborted) throw new Error('oidc.session_changed', { cause: error });
      if (timedOut || error instanceof TypeError) {
        throw new BrowserOidcTemporaryError(error);
      }
      throw error;
    } finally {
      clearTimeout(timer);
      controller.signal.removeEventListener('abort', abort);
    }
  }

  private async discovery(): Promise<Discovery> {
    if (this.metadata) return this.metadata;
    const data = await this.json(`${this.config.issuer.replace(/\/$/, '')}/.well-known/openid-configuration`);
    if (data.issuer !== this.config.issuer) throw new Error('oidc.issuer_mismatch');
    const metadata: Discovery = {
      issuer: this.config.issuer,
      authorization_endpoint: this.endpoint(data.authorization_endpoint),
      token_endpoint: this.endpoint(data.token_endpoint),
      jwks_uri: this.endpoint(data.jwks_uri),
      end_session_endpoint: data.end_session_endpoint === undefined ? undefined : this.endpoint(data.end_session_endpoint),
    };
    this.metadata = metadata;
    return metadata;
  }

  async beginSignIn(returnTo?: string | null, locale?: string): Promise<string> {
    this.clear();
    const epoch = this.epoch;
    const metadata = await this.discovery();
    const verifier = this.random();
    const challenge = await createPkceChallenge(verifier, this.options.crypto);
    if (epoch !== this.epoch) throw new Error('oidc.session_changed');
    const pending: PendingLogin = {
      issuer: this.config.issuer,
      clientId: this.config.clientId,
      redirectUri: `${this.options.origin}/auth/callback`,
      state: this.random(), nonce: this.random(), verifier,
      createdAt: this.now(), returnTo: normalizeReturnToPath(returnTo),
    };
    // Storage failure prevents navigation: never start a login we cannot bind.
    this.options.storage.setItem(PENDING_KEY, JSON.stringify(pending));
    const url = new URL(metadata.authorization_endpoint);
    url.searchParams.set('client_id', this.config.clientId);
    url.searchParams.set('redirect_uri', pending.redirectUri);
    url.searchParams.set('response_type', 'code');
    url.searchParams.set('response_mode', 'query');
    url.searchParams.set('scope', 'openid profile email');
    url.searchParams.set('state', pending.state);
    url.searchParams.set('nonce', pending.nonce);
    url.searchParams.set('code_challenge', challenge);
    url.searchParams.set('code_challenge_method', 'S256');
    if (locale === 'cs' || locale === 'en') url.searchParams.set('ui_locales', locale);
    return url.toString();
  }

  completeSignIn(search: string, hash = ''): Promise<string | undefined> {
    const params = new URLSearchParams(search);
    const hashParams = new URLSearchParams(hash.replace(/^#/, ''));
    if (['access_token', 'token', 'id_token'].some((key) => params.has(key) || hashParams.has(key))) {
      return Promise.reject(new Error('oidc.implicit_rejected'));
    }
    const key = `${search}|${hash}`;
    // React StrictMode must not exchange a one-time code twice.
    if (this.completion?.key === key) return this.completion.promise;
    const promise = this.exchange(params);
    this.completion = { key, promise };
    return promise;
  }

  private async exchange(params: URLSearchParams): Promise<string | undefined> {
    const epoch = this.epoch;
    const raw = this.options.storage.getItem(PENDING_KEY);
    this.options.storage.removeItem(PENDING_KEY);
    if (!raw) throw new Error('oidc.missing_transaction');
    const pending = object(JSON.parse(raw));
    if (pending.issuer !== this.config.issuer || pending.clientId !== this.config.clientId ||
        pending.redirectUri !== `${this.options.origin}/auth/callback` ||
        pending.state !== params.get('state') || typeof pending.state !== 'string' || pending.state.length < 32 ||
        typeof pending.createdAt !== 'number' || pending.createdAt > this.now() || this.now() - pending.createdAt > TRANSACTION_TTL_MS ||
        (params.has('iss') && params.get('iss') !== this.config.issuer)) {
      throw new Error('oidc.invalid_transaction');
    }
    if (params.has('error')) throw new Error('oidc.login_cancelled');
    const metadata = await this.discovery();
    const response = await this.tokenRequest(metadata, new URLSearchParams({
      grant_type: 'authorization_code', client_id: this.config.clientId,
      code: text(params.get('code')), redirect_uri: text(pending.redirectUri),
      code_verifier: text(pending.verifier),
    }));
    const tokens = await this.validateTokens(response, text(pending.nonce));
    if (epoch !== this.epoch) throw new Error('oidc.session_changed');
    this.tokens = tokens;
    return normalizeReturnToPath(typeof pending.returnTo === 'string' ? pending.returnTo : undefined);
  }

  private tokenRequest(metadata: Discovery, body: URLSearchParams) {
    return this.json(metadata.token_endpoint, {
      method: 'POST', headers: { 'content-type': 'application/x-www-form-urlencoded', accept: 'application/json' }, body,
    });
  }

  private async validateIdToken(idToken: string, nonce: string, previousSubject?: string): Promise<string> {
    const parts = idToken.split('.');
    if (parts.length !== 3) throw new Error('oidc.invalid_id_token');
    const header = object(JSON.parse(new TextDecoder().decode(decodeBase64Url(parts[0]!))));
    // Explicit supported signing contract for the Keycloak baseline.
    if (header.alg !== 'RS256') throw new Error('oidc.unsupported_signature');
    const kid = text(header.kid);
    const metadata = await this.discovery();
    const jwks = await this.json(metadata.jwks_uri);
    if (!Array.isArray(jwks.keys)) throw new Error('oidc.invalid_keys');
    const candidates = jwks.keys.map(object).filter((key) => key.kid === kid && key.kty === 'RSA' &&
      (key.use === undefined || key.use === 'sig') && (key.alg === undefined || key.alg === 'RS256'));
    if (candidates.length !== 1) throw new Error('oidc.invalid_keys');
    const key = await this.options.crypto.subtle.importKey('jwk', candidates[0] as JsonWebKey,
      { name: 'RSASSA-PKCS1-v1_5', hash: 'SHA-256' }, false, ['verify']);
    const valid = await this.options.crypto.subtle.verify('RSASSA-PKCS1-v1_5', key,
      new Uint8Array(decodeBase64Url(parts[2]!)), new TextEncoder().encode(`${parts[0]}.${parts[1]}`));
    if (!valid) throw new Error('oidc.invalid_signature');
    const claims = object(JSON.parse(new TextDecoder().decode(decodeBase64Url(parts[1]!))));
    const audience = typeof claims.aud === 'string' ? [claims.aud] : claims.aud;
    const nowSeconds = this.now() / 1000;
    if (claims.iss !== this.config.issuer || !Array.isArray(audience) || !audience.includes(this.config.clientId) ||
        (audience.length > 1 && claims.azp !== this.config.clientId) ||
        (claims.azp !== undefined && claims.azp !== this.config.clientId) ||
        typeof claims.exp !== 'number' || !Number.isFinite(claims.exp) || claims.exp <= nowSeconds - CLOCK_SKEW_SECONDS ||
        typeof claims.iat !== 'number' || !Number.isFinite(claims.iat) || claims.iat > nowSeconds + CLOCK_SKEW_SECONDS ||
        (!previousSubject && claims.nonce !== nonce) ||
        (previousSubject && claims.nonce !== undefined && claims.nonce !== nonce)) {
      throw new Error('oidc.invalid_claims');
    }
    const subject = text(claims.sub);
    if (previousSubject && previousSubject !== subject) throw new Error('oidc.subject_changed');
    return subject;
  }

  private async validateTokens(response: Record<string, unknown>, nonce: string, previous?: LiveTokens, receivedAt = this.now()): Promise<LiveTokens> {
    if (text(response.token_type).toLowerCase() !== 'bearer' ||
        typeof response.expires_in !== 'number' || !Number.isFinite(response.expires_in) || response.expires_in <= 0) {
      throw new Error('oidc.invalid_tokens');
    }
    const idToken = response.id_token === undefined && previous ? previous.idToken : text(response.id_token);
    const subject = response.id_token === undefined && previous ? previous.subject :
      await this.validateIdToken(idToken, nonce, previous?.subject);
    return {
      accessToken: text(response.access_token),
      refreshToken: response.refresh_token === undefined ? previous?.refreshToken : text(response.refresh_token),
      idToken, subject, nonce, expiresAt: receivedAt + response.expires_in * 1000,
    };
  }

  async getAccessToken(): Promise<string> {
    const tokens = this.tokens;
    if (!tokens) throw new Error('oidc.session_expired');
    if (tokens.expiresAt - this.now() > 30_000) return tokens.accessToken;
    if (this.refreshFlight) return this.refreshFlight;
    const epoch = this.epoch;
    const flight = (async () => {
      if (!tokens.refreshToken) throw new Error('oidc.session_expired');
      const metadata = await this.discovery();
      let pending = this.pendingRefresh;
      if (!pending || pending.previous !== tokens) {
        const response = await this.tokenRequest(metadata, new URLSearchParams({
          grant_type: 'refresh_token', client_id: this.config.clientId, refresh_token: tokens.refreshToken,
        }));
        if (epoch !== this.epoch) throw new Error('oidc.session_changed');
        // A token endpoint can rotate the refresh credential before JWKS loads.
        // Keep this response only in memory so a transient JWKS outage retries
        // validation, not the already consumed refresh credential.
        pending = { response, previous: tokens, receivedAt: this.now() };
        this.pendingRefresh = pending;
      }
      const refreshed = await this.validateTokens(pending.response, tokens.nonce, tokens, pending.receivedAt);
      if (epoch !== this.epoch) throw new Error('oidc.session_changed');
      this.tokens = refreshed;
      this.pendingRefresh = undefined;
      // A delayed JWKS retry must not extend the token's original lifetime.
      // Retain the validated rotated credential for the next renewal, but do
      // not release an access token that expired while validation was blocked.
      if (refreshed.expiresAt <= this.now()) throw new BrowserOidcTemporaryError();
      return refreshed.accessToken;
    })();
    this.refreshFlight = flight;
    try {
      return await flight;
    } catch (error) {
      if (epoch === this.epoch && error instanceof BrowserOidcTemporaryError) {
        throw error;
      }
      if (epoch === this.epoch) this.clear();
      throw new Error('oidc.session_expired', { cause: error });
    } finally {
      if (this.refreshFlight === flight) this.refreshFlight = undefined;
    }
  }

  logoutUrl(): string | undefined {
    const idToken = this.tokens?.idToken;
    const endpoint = this.metadata?.end_session_endpoint;
    this.clear();
    if (!endpoint || !idToken) return undefined;
    const url = new URL(endpoint);
    url.searchParams.set('id_token_hint', idToken);
    url.searchParams.set('client_id', this.config.clientId);
    url.searchParams.set('post_logout_redirect_uri', `${this.options.origin}/signin`);
    return url.toString();
  }

  clear(): void {
    this.epoch += 1;
    this.controller.abort();
    this.controller = new AbortController();
    this.tokens = undefined;
    this.refreshFlight = undefined;
    this.pendingRefresh = undefined;
    this.completion = undefined;
    try { this.options.storage.removeItem(PENDING_KEY); } catch { /* Tokens are already invalidated; storage may be unavailable. */ }
  }
}
